"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.WrapDriverContext = void 0;
exports.wrapRun = wrapRun;
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const localizeUtils_1 = require("../../../common/localizeUtils");
const componentError_1 = require("../../error/componentError");
const teamsFxTelemetryReporter_1 = require("../../utils/teamsFxTelemetryReporter");
const constants_1 = require("../aad/utility/constants");
class WrapDriverContext {
    progressBars = [];
    summaries = [];
    eventName;
    telemetryProperties;
    wrapTelemetryReporter;
    constructor(driverContext, eventName, componentName) {
        Object.assign(this, driverContext, {});
        this.eventName = eventName;
        this.telemetryProperties = {
            component: componentName.replace(/\//g, ""), // Remove `/` in the componentName to avoid the value being redacted.
        };
        if (driverContext.telemetryReporter) {
            this.wrapTelemetryReporter = new teamsFxTelemetryReporter_1.TeamsFxTelemetryReporter(driverContext.telemetryReporter, {
                componentName,
            });
        }
    }
    async createProgressBar(title, steps) {
        const progressBar = this.ui?.createProgressBar(title, steps);
        if (progressBar) {
            this.progressBars.push(progressBar);
            await progressBar.start();
            await progressBar.next();
        }
        return progressBar;
    }
    async endProgressBars(success) {
        await Promise.all(this.progressBars.map(async (progressbar) => {
            await progressbar.end(success);
        }));
    }
    addTelemetryProperties(properties) {
        this.telemetryProperties = { ...properties, ...this.telemetryProperties };
    }
    addSummary(...summaries) {
        this.summaries.push(...summaries);
    }
}
exports.WrapDriverContext = WrapDriverContext;
async function wrapRun(context, exec, isExecute) {
    const eventName = context.eventName;
    try {
        let actionRes;
        context.wrapTelemetryReporter?.sendStartEvent({ eventName });
        context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.startExecuteDriver, eventName));
        const res = await exec();
        context.wrapTelemetryReporter?.sendEndEvent({
            eventName,
            properties: context.telemetryProperties,
        });
        context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.successExecuteDriver, eventName));
        await context.endProgressBars(true);
        if (isExecute) {
            actionRes = { result: (0, teamsfx_api_1.ok)(res), summaries: context.summaries };
        }
        else {
            actionRes = (0, teamsfx_api_1.ok)(res);
        }
        return actionRes;
    }
    catch (error) {
        let actionRes;
        const fxError = getError(context, error);
        context.wrapTelemetryReporter?.sendEndEvent({
            eventName,
            properties: context.telemetryProperties,
        }, fxError);
        await context.endProgressBars(false);
        if (isExecute) {
            actionRes = { result: (0, teamsfx_api_1.err)(fxError), summaries: context.summaries };
        }
        else {
            actionRes = (0, teamsfx_api_1.err)(fxError);
        }
        return actionRes;
    }
}
const ErrorConstants = {
    unhandledError: "UnhandledError",
    unhandledErrorMessage: "Unhandled Error",
};
function getError(context, error) {
    let fxError;
    if (error instanceof componentError_1.BaseComponentInnerError) {
        fxError = error.toFxError();
    }
    else if (error instanceof teamsfx_api_1.UserError || error instanceof teamsfx_api_1.SystemError) {
        fxError = error;
    }
    else {
        if (!(error instanceof Error)) {
            error = new Error(error.toString());
        }
        fxError = new teamsfx_api_1.SystemError({
            error,
            source: context.eventName,
            name: ErrorConstants.unhandledError,
            message: error.message,
            displayMessage: error.message,
        });
    }
    return fxError;
}
//# sourceMappingURL=wrapUtil.js.map